import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import OrgMemberDialog from "../components/OrgMemberDialog";
import OrgTree from "../components/OrgTree";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { drawLetterhead, drawFooterWave, contentBottom } from "../lib/docTemplate";
import { Network, Plus, Pencil, Trash2, Users, GitBranch, Download, Settings2, Power, X } from "lucide-react";

const EST_BADGE = {
  activo: "bg-green-50 text-green-700 border-green-200",
  inactivo: "bg-slate-100 text-slate-500 border-slate-200",
  vacante: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function OrgChart() {
  const { isPM } = useAuth();
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState({ departamentos: [], puestos: [] });
  const [fDept, setFDept] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const treeRef = useRef(null);

  const load = () => api.get("/org/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); api.get("/org/catalog").then((r) => setCatalog(r.data)); }, []);

  const nameById = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m.nombre])), [members]);
  const deptColorMap = useMemo(() => Object.fromEntries((catalog.departamentos || []).map((d) => [d.name, d.color])), [catalog]);
  const filtered = useMemo(() => members.filter((m) => fDept === "all" || m.departamento === fDept).sort((a, b) => (a.orden || 0) - (b.orden || 0)), [members, fDept]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (m) => { setEditing(m); setDialogOpen(true); };

  const toggleEstatus = async (m) => {
    const nuevo = m.estatus === "activo" ? "inactivo" : "activo";
    try { await api.put(`/org/members/${m.id}`, { estatus: nuevo }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/org/members/${toDelete.id}`); toast.success("Integrante eliminado"); setToDelete(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportPDF = async () => {
    if (!treeRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(treeRef.current, { scale: 3, backgroundColor: "#ffffff", useCORS: true });
      const imgW = canvas.width;
      const imgH = canvas.height;
      const landscape = imgW >= imgH;
      const pdf = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const margin = 40;
      // Membrete Gerencia TI + onda roja
      const contentTop = drawLetterhead(pdf, { title: "Organigrama — Tecnologías de la Información" });
      drawFooterWave(pdf);
      const bottom = contentBottom(pdf);
      // Reescalar TODO el organigrama para que quepa exacto en una hoja (sin cortar)
      const availW = pw - margin * 2;
      const availH = bottom - contentTop;
      const ratio = Math.min(availW / imgW, availH / imgH);
      const w = imgW * ratio;
      const h = imgH * ratio;
      const x = (pw - w) / 2;
      const y = contentTop + (availH - h) / 2;
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", x, y, w, h, undefined, "SLOW");
      pdf.save(`organigrama_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      toast.error("Error al generar PDF");
    } finally { setExporting(false); }
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Personal TI</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2">
          <Network className="h-7 w-7" /> Organigrama
        </h1>
      </div>

      <div className="p-8">
        <Tabs defaultValue="gestion">
          <TabsList className="rounded-sm bg-slate-100 mb-6">
            <TabsTrigger value="gestion" data-testid="org-tab-gestion" className="rounded-sm data-[state=active]:bg-white"><Users className="h-4 w-4 mr-1.5" />Gestión de personal</TabsTrigger>
            <TabsTrigger value="visual" data-testid="org-tab-visual" className="rounded-sm data-[state=active]:bg-white"><GitBranch className="h-4 w-4 mr-1.5" />Visualización gráfica</TabsTrigger>
          </TabsList>

          {/* Gestión */}
          <TabsContent value="gestion">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <Select value={fDept} onValueChange={setFDept}>
                <SelectTrigger data-testid="org-filter-dept" className="rounded-sm w-56 h-9"><SelectValue placeholder="Departamento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los departamentos</SelectItem>
                  {(catalog.departamentos || []).map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {isPM && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={() => setCatOpen(true)} data-testid="org-catalog-btn"><Settings2 className="h-4 w-4 mr-1.5" />Departamentos y puestos</Button>
                  <Button className="rounded-sm h-9 bg-slate-900 hover:bg-slate-800" onClick={openNew} data-testid="org-new-btn"><Plus className="h-4 w-4 mr-1.5" />Nuevo integrante</Button>
                </div>
              )}
            </div>

            <div className="border border-border bg-white rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-border text-left text-[11px] font-bold uppercase tracking-wide text-slate-600">
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Puesto</th>
                    <th className="px-3 py-2">Departamento</th>
                    <th className="px-3 py-2">Jefe directo</th>
                    <th className="px-3 py-2">Nivel</th>
                    <th className="px-3 py-2">Estatus</th>
                    {isPM && <th className="px-3 py-2 text-right">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => (
                    <tr key={m.id} data-testid={`org-row-${m.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{m.estatus === "vacante" ? <span className="italic text-slate-400">VACANTE</span> : m.nombre}</td>
                      <td className="px-3 py-2 text-slate-600">{m.puesto}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: deptColorMap[m.departamento] || "#94a3b8" }} />
                          {m.departamento}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">{m.jefe_id ? nameById[m.jefe_id] || "—" : "—"}</td>
                      <td className="px-3 py-2 font-mono text-slate-500">{m.nivel}</td>
                      <td className="px-3 py-2"><span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase ${EST_BADGE[m.estatus]}`}>{m.estatus}</span></td>
                      {isPM && (
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => toggleEstatus(m)} title="Activar/Desactivar" data-testid={`org-toggle-est-${m.id}`} className="text-slate-300 hover:text-slate-700 p-1"><Power className="h-4 w-4" /></button>
                            <button onClick={() => openEdit(m)} title="Editar" data-testid={`org-edit-${m.id}`} className="text-slate-300 hover:text-slate-700 p-1"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => setToDelete(m)} title="Eliminar" data-testid={`org-del-${m.id}`} className="text-slate-300 hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={isPM ? 7 : 6} className="text-center text-slate-400 py-8">Sin integrantes.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Visualización */}
          <TabsContent value="visual">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                {(catalog.departamentos || []).map((d) => (
                  <span key={d.name} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: d.color }} />{d.name}
                  </span>
                ))}
              </div>
              <Button className="rounded-sm h-9 bg-slate-900 hover:bg-slate-800" onClick={exportPDF} disabled={exporting} data-testid="org-export-pdf-btn"><Download className="h-4 w-4 mr-1.5" />{exporting ? "Generando..." : "Exportar PDF"}</Button>
            </div>
            <div className="border border-border rounded-sm">
              <OrgTree members={members.filter((m) => m.estatus !== "inactivo")} deptColorMap={deptColorMap} departamentos={catalog.departamentos} forwardRef={treeRef} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <OrgMemberDialog open={dialogOpen} onOpenChange={setDialogOpen} member={editing} members={members} catalog={catalog} onSaved={load} />

      <CatalogManagerDialog open={catOpen} onOpenChange={setCatOpen} catalog={catalog} onSaved={(c) => setCatalog(c)} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar integrante?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{toDelete?.nombre}</strong>. Sus subordinados se reasignarán a su jefe. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="org-confirm-delete" onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CatalogManagerDialog({ open, onOpenChange, catalog, onSaved }) {
  const [deptos, setDeptos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [newColor, setNewColor] = useState("#334155");
  const [newPuesto, setNewPuesto] = useState("");

  const orgUid = () => Math.random().toString(36).slice(2, 10);
  useEffect(() => { if (open) { setDeptos((catalog.departamentos || []).map((d) => ({ ...d, _k: orgUid() }))); setPuestos(catalog.puestos || []); } }, [open, catalog]);

  const save = async () => {
    try { const { data } = await api.put("/org/catalog", { departamentos: deptos.map(({ _k, ...d }) => d), puestos }); onSaved(data); toast.success("Guardado"); onOpenChange(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="org-catalog-dialog">
        <DialogHeader><DialogTitle>Departamentos y puestos</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Departamentos</p>
            <div className="space-y-1.5">
              {deptos.map((d, i) => (
                <div key={d._k} className="flex items-center gap-2">
                  <input type="color" value={d.color} onChange={(e) => setDeptos((prev) => prev.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} className="h-8 w-10 rounded-sm border border-slate-200" />
                  <Input value={d.name} onChange={(e) => setDeptos((prev) => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="rounded-sm h-8" />
                  <button onClick={() => setDeptos((prev) => prev.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-8 w-10 rounded-sm border border-slate-200" />
              <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="Nuevo departamento" className="rounded-sm h-8" data-testid="org-new-dept-input" />
              <Button variant="outline" className="rounded-sm h-8" onClick={() => { if (newDept.trim()) { setDeptos((p) => [...p, { _k: orgUid(), name: newDept.trim(), color: newColor }]); setNewDept(""); } }}>Agregar</Button>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Puestos</p>
            <div className="flex flex-wrap gap-1.5">
              {puestos.map((p, i) => (
                <span key={p} className="inline-flex items-center gap-1 bg-slate-100 rounded-sm px-2 py-1 text-xs">
                  {p}<button onClick={() => setPuestos((prev) => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Input value={newPuesto} onChange={(e) => setNewPuesto(e.target.value)} placeholder="Nuevo puesto" className="rounded-sm h-8" data-testid="org-new-puesto-input" />
              <Button variant="outline" className="rounded-sm h-8" onClick={() => { if (newPuesto.trim()) { setPuestos((p) => [...p, newPuesto.trim()]); setNewPuesto(""); } }}>Agregar</Button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="org-catalog-save">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
